import { getQueryParameterFromUri } from "@WhiteCap/Common/Utillities/utils";
import {
    SubscribedEmailDto,
    SubscriptionsDashboardState,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import { ApiResultModel as OrderCompleteApiResultModel } from "@WhiteCap/Store/Service/OsnSubscriptionsServices/OrderCompleteSubscribersService";
import { Draft } from "immer";

const orderCompleteSubscriptions = {
    "Custom/SubscriptionDashboard/BeginOcSubscribersDataLoad": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = true;
    },
    "Custom/SubscriptionDashboard/OcSubscribersDataLoadError": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/CompleteOcSubscribersDataLoad": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: OrderCompleteApiResultModel;
        },
    ) => {
        const customerId = getQueryParameterFromUri(action.result?.uri, "customerId");
        const data = {
            properties: action.result.properties,
            subscribedEmails: action.result.subscribedEmails,
            count: action.result.ocOrderStatusSummary?.count,
        };
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.ocOrderStatusSummary = data;
        } else if (draft.osnType === "individualJobSiteLevel" && customerId) {
            draft.jobSiteSubscriptionsById[customerId].ocOrderStatusSummary = data;
        }
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/CompleteRemoveSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: string;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.ocOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);

            draft.accountLevelSubscriptions.ocOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.ocOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count: (draft.accountLevelSubscriptions.ocOrderStatusSummary?.count || 0) - 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary?.count ||
                        0) - 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/CompleteAddSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: SubscribedEmailDto;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.ocOrderStatusSummary?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.accountLevelSubscriptions.ocOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.ocOrderStatusSummary,
                subscribedEmails: emails,
                count: (draft.accountLevelSubscriptions.ocOrderStatusSummary?.count || 0) + 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary
                    ?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary,
                subscribedEmails: emails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary?.count ||
                        0) + 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/CompleteUpdatingSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result:  {
                customerId: string;
                subscribedEmailDto: SubscribedEmailDto;
            };
        },
    ) => {
        const updatedEmailId = action.result.subscribedEmailDto.email;
        const getUpdatedEmailDtos = (updatedEmailId: string, emailsDto: SubscribedEmailDto[]) => {
            const updatedSubscribedEmails = emailsDto?.map(emailDto => {
                let updatedEmailDto: SubscribedEmailDto = emailDto;
                if (emailDto.email === updatedEmailId) {
                    updatedEmailDto = { ...action.result.subscribedEmailDto };
                }
                return updatedEmailDto;
            });
            return updatedSubscribedEmails;
        };
        if (draft.osnType === "accountLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.accountLevelSubscriptions.ocOrderStatusSummary?.subscribedEmails || [],
            );
            draft.accountLevelSubscriptions.ocOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.ocOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary
                    ?.subscribedEmails || [],
            );
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/CompleteRemovePhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                emailId: string;
            };
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] = draft.accountLevelSubscriptions.ocOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.accountLevelSubscriptions.ocOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.ocOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].ocOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/BeginApplyOcSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = true;
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/ApplyOcSubscriptionsError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/OcSubscriptions/CompleteApplyOcSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
};

export default orderCompleteSubscriptions;
